import { AuthService } from './services/auth'
import { BrowserUtility } from './browser-utility'
import { StorageConstant, ImpersonationStorage } from './constant'

/**
 * Low-level helpers for the Super Admin "Login as this user" flow.
 *
 * Rules baked in here (see Impersonation UI handoff):
 *   - The active bearer token keeps living in localStorage, exactly like a
 *     normal login, so every existing request picks it up unchanged.
 *   - The admin's pre-impersonation token + user are stashed in sessionStorage
 *     only (never localStorage): they are sensitive and must not survive a
 *     tab-close.
 *   - Because this app has no central query cache, callers swap the token/user
 *     and then hard-reload so every store re-hydrates as the new identity.
 */
export class ImpersonationUtility {
    static getSession() {
        const raw = window.sessionStorage.getItem(ImpersonationStorage.session)
        if (!raw) return null
        try {
            return JSON.parse(raw)
        } catch {
            return null
        }
    }

    static setSession(session) {
        window.sessionStorage.setItem(
            ImpersonationStorage.session,
            JSON.stringify(session),
        )
    }

    static isImpersonating() {
        return !!this.getSession()
    }

    // Stash the admin identity so we can restore it on end/expiry.
    static stashAdmin() {
        window.sessionStorage.setItem(
            ImpersonationStorage.adminToken,
            AuthService.getToken(),
        )
        const adminUser = AuthService.getUser()
        if (adminUser) {
            window.sessionStorage.setItem(
                ImpersonationStorage.adminUser,
                JSON.stringify(adminUser),
            )
        }
    }

    static getStashedAdminToken() {
        return window.sessionStorage.getItem(ImpersonationStorage.adminToken)
    }

    static getStashedAdminUser() {
        const raw = window.sessionStorage.getItem(ImpersonationStorage.adminUser)
        if (!raw) return null
        try {
            return JSON.parse(raw)
        } catch {
            return null
        }
    }

    static clearStash() {
        window.sessionStorage.removeItem(ImpersonationStorage.adminToken)
        window.sessionStorage.removeItem(ImpersonationStorage.adminUser)
        window.sessionStorage.removeItem(ImpersonationStorage.session)
    }

    // Persist the impersonated identity as the active session and remember the
    // hard-cap so the banner can count down and the interceptor can react.
    static applyTarget(token, targetUser, session) {
        BrowserUtility.save(StorageConstant.token, token)
        if (targetUser) {
            BrowserUtility.saveObj(StorageConstant.user, {
                ...targetUser,
                IsImpersonating: true,
            })
        }
        this.setSession(session)
    }

    // Swap the fresh admin token (preferred) or the stashed one back in, restore
    // the admin user, and drop every impersonation artifact.
    static restoreAdmin(freshAdminToken) {
        const token = freshAdminToken || this.getStashedAdminToken()
        if (token) {
            BrowserUtility.save(StorageConstant.token, token)
        }
        const adminUser = this.getStashedAdminUser()
        if (adminUser) {
            BrowserUtility.saveObj(StorageConstant.user, adminUser)
        }
        this.clearStash()
    }

    // Pull the login-shaped user out of an ImpersonateStart response, tolerating
    // a few likely shapes since the exact contract can vary by environment.
    static extractUser(response) {
        if (!response || typeof response !== 'object') return null
        const candidate = response.User || response.user || response
        if (candidate && (candidate.Email || candidate.FullName || candidate.CompanyId)) {
            return candidate
        }
        return null
    }
}
